import React from 'react';
import Modal from '../helpers/Modal';
import { KnightKey } from '../../types';
import { SWORN_DATA } from '../../gameData';
import { Hammer, Check } from 'lucide-react';
import { capitalize } from '../../utils/capitalize';

interface LoadoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedKnight: KnightKey | null;
    selectedWeapon: string | null;
    setSelectedWeapon: (w: string) => void;
    selectedSpell: string | null;
    setSelectedSpell: (s: string) => void;
}

const LoadoutModal: React.FC<LoadoutModalProps> = ({ 
    isOpen, 
    onClose, 
    selectedKnight, 
    selectedWeapon, 
    setSelectedWeapon, 
    selectedSpell, 
    setSelectedSpell 
}) => {
    
    const renderDropdown = (label: string, items: string[], selectedItem: string | null, setter: (item: string) => void) => (
        <div className="flex flex-col gap-2">
            <label htmlFor={label.toLowerCase()} className="text-sm font-medium text-sworn-gold">{label}</label>
            <select
                id={label.toLowerCase()}
                value={selectedItem || ''}
                onChange={(e) => setter(e.target.value)}
                className="p-3 rounded-lg bg-slate-800 text-white border border-sworn-brown focus:ring-2 focus:ring-sworn-gold focus:border-transparent appearance-none cursor-pointer capitalize"
            >
                <option value="" disabled>{`Select ${label.toLowerCase()}`}</option>
                {items.map((item) => <option key={item} value={item}>{item.split('_').map(capitalize).join(' ')}</option>)}
            </select>
        </div>
    );

    const knightData = selectedKnight ? SWORN_DATA.classes[selectedKnight] : null;
    const currentWeaponData = (selectedWeapon && knightData) ? knightData.weapons[selectedWeapon as keyof typeof knightData.weapons] : null;
    const currentSpellData = (selectedSpell && knightData) ? knightData.spells[selectedSpell as keyof typeof knightData.spells] : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`${capitalize(selectedKnight || 'Unknown')}'s Loadout`} size="2xl">
            {!selectedKnight || !knightData ? (
                <p className="text-slate-400 mt-4 text-center">Select a Knight to see weapon and spell details.</p>
            ) : (
                <div className="mt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {renderDropdown('Weapon', Object.keys(knightData.weapons), selectedWeapon, setSelectedWeapon)}
                        {renderDropdown('Spell', Object.keys(knightData.spells), selectedSpell, setSelectedSpell)}
                    </div>

                    {(currentWeaponData || currentSpellData) && (
                        <div className="mt-6 p-4 bg-slate-800/80 rounded-lg border border-sworn-brown max-h-[40vh] overflow-y-auto">
                            <h4 className="text-xl font-semibold text-sworn-gold mb-3 flex items-center gap-2">
                                <Hammer className='w-5 h-5' /> Anvil Upgrades
                            </h4>
                            <div className="space-y-4">
                                {currentWeaponData && (
                                    <div>
                                        <p className="text-lg font-medium text-white mb-2 underline capitalize">{selectedWeapon?.split('_').join(' ')} Anvil Upgrades:</p>
                                        <div className="grid grid-cols-1 gap-3">
                                            {currentWeaponData.anvil_upgrades.map((upgrade) => (
                                                <div key={upgrade.name} className="bg-slate-900/50 p-3 rounded-md border border-slate-700">
                                                    <p className="font-semibold text-sm text-yellow-400">{upgrade.name}</p>
                                                    <p className="text-xs text-slate-400">{upgrade.effect}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {currentSpellData && (
                                    <div className="pt-4 border-t border-slate-700">
                                        <p className="text-lg font-medium text-white mb-2 underline capitalize">{selectedSpell?.split('_').join(' ')} Anvil Upgrades:</p>
                                        <div className="grid grid-cols-1 gap-3">
                                            {currentSpellData.anvil_upgrades.map((upgrade) => (
                                                <div key={upgrade.name} className="bg-slate-900/50 p-3 rounded-md border border-slate-700">
                                                    <p className="font-semibold text-sm text-yellow-400">{upgrade.name}</p>
                                                    <p className="text-xs text-slate-400">{upgrade.effect}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <button onClick={onClose} className="mt-6 w-full bg-sworn-gold hover:bg-sworn-gold-dark text-black font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                        <Check className="w-5 h-5" /> Confirm Loadout
                    </button>
                </div>
            )}
        </Modal>
    );
};

export default LoadoutModal;